import { Checkbox, DatePicker, Input, Modal } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo } from "react";
import * as Yup from "yup";
import { useUpdateSavingsGoal } from "../../api/api.update";
import { useGetGroupUsers2 } from "../../api/api.get";
import { useAuthContext } from "../../providers/auth.provider";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { ISavingsGoal } from "../../types/types";
import { capitalize } from "../../utils/functions";

export default function EditSavingsGoalDialog({
  open,
  goal,
  onCancel,
  onSuccess,
  onError,
}: {
  open: boolean;
  goal?: ISavingsGoal;
  onCancel: () => void;
  onSuccess: () => void;
  onError: () => void;
}) {
  const { user } = useAuthContext();
  const { defaultGroupId } = useGroupContext();
  const { currency } = useCurrencyContext();

  const { data: groupUsers } = useGetGroupUsers2(defaultGroupId);
  const { mutateAsync, isPending } = useUpdateSavingsGoal(defaultGroupId);

  const formik = useFormik({
    initialValues: {
      name: goal?.name ?? "",
      targetAmount: goal?.targetAmount ?? 0,
      targetDate: goal?.targetDate ? dayjs(goal.targetDate) : undefined,
      usernames: goal?.users ?? [],
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter a goal name"),
      targetAmount: Yup.number()
        .typeError("Please enter a valid number")
        .positive("Amount must be greater than zero")
        .required("Please enter a target amount"),
      targetDate: Yup.mixed().required("Please select a target date"),
    }),
    onSubmit: async (values) => {
      mutateAsync({
        goalId: goal!.id,
        name: values.name,
        targetAmount: +values.targetAmount,
        targetDate: values.targetDate!.format("YYYY-MM-DD"),
        users: values.usernames,
      })
        .then(() => onSuccess())
        .catch(() => onError());
    },
  });

  const userOptions = useMemo(() => {
    if (!groupUsers) return [];
    return groupUsers
      .filter((gu) => gu.username !== user?.username)
      .map((gu) => ({
        label: capitalize(gu.firstName || gu.username),
        value: gu.username,
      }));
  }, [groupUsers, user]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Modal
        title="Edit Savings Goal"
        open={open}
        onOk={formik.submitForm}
        onCancel={onCancel}
        okText="Save"
        okButtonProps={{ loading: isPending }}
      >
        <div className="flex flex-col gap-4 my-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Goal Name *
            </label>
            <Input
              placeholder="e.g., Emergency Fund"
              {...formik.getFieldProps("name")}
              status={formik.errors.name ? "error" : undefined}
            />
          </div>

          {/* Target amount */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Target Amount *
            </label>
            <Input
              type="number"
              prefix={currency}
              placeholder="e.g., 10000"
              {...formik.getFieldProps("targetAmount")}
              status={formik.errors.targetAmount ? "error" : undefined}
            />
          </div>

          {/* Target date */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Target Date *
            </label>
            <DatePicker
              className="w-full"
              value={formik.values.targetDate}
              onChange={(date) => formik.setFieldValue("targetDate", date)}
            />
          </div>

          {/* Share with */}
          <div>
            <p className="text-sm font-semibold mb-2">Share With?</p>
            {userOptions.length > 0 ? (
              <Checkbox.Group
                options={userOptions}
                value={formik.values.usernames}
                onChange={(values) => formik.setFieldValue("usernames", values)}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                No other members in this group to share with.
              </p>
            )}
          </div>
        </div>
      </Modal>
    </form>
  );
}
